import { CoreEvent } from '@carvana/core-events';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { EditOrderModal } from '|/app/navigation-rail/components/modals';
import {
  VERIFX_OPEN_EDIT_ORDER_MODAL,
  coreEventOffWithType,
  type VerifxModalEvents
} from '|/app/verifx-events';
import { isNativeWebview } from '|/helpers/is-native-app';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import sendNativeEvent from '|/helpers/send-native-event';

/**
 * Hosts the edit-order modal for surfaces that can't render it themselves, opening it on
 * `VERIFX_OPEN_EDIT_ORDER_MODAL` and taking its content from that event's payload.
 */
const EditOrderModalListener: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [targetStep, setTargetStep] = useState<string | undefined>();
  const [deliveryType, setDeliveryType] = useState<string | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const handler = (event: VerifxModalEvents['VERIFX_OPEN_EDIT_ORDER_MODAL']) => {
      setEditingSectionName(event.editingSectionName ?? '');
      setTargetStep(event.targetStep);
      setDeliveryType(event.deliveryType ?? null);
      setIsOpen(true);
    };
    CoreEvent.onWithType<VerifxModalEvents, typeof VERIFX_OPEN_EDIT_ORDER_MODAL>(
      VERIFX_OPEN_EDIT_ORDER_MODAL,
      handler
    );
    return () => {
      coreEventOffWithType<VerifxModalEvents, typeof VERIFX_OPEN_EDIT_ORDER_MODAL>(
        VERIFX_OPEN_EDIT_ORDER_MODAL,
        handler
      );
    };
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setEditingSectionName('');
    setTargetStep(undefined);
    setDeliveryType(null);
    if (isNativeWebview() && pathname.includes('order-placed')) {
      sendNativeEvent({
        event: MobileWebviewEvents.TOGGLE_CHAT_UI,
        payload: { shouldShow: true },
        source: 'purchase'
      });
    }
  }, [pathname]);

  return (
    <EditOrderModal
      isOpen={isOpen}
      onClose={handleClose}
      editingSectionName={editingSectionName}
      targetStep={targetStep}
      deliveryPreference={deliveryType}
    />
  );
};

export default EditOrderModalListener;
